import {
  addQueryParams,
  buildUseQuery,
  Skeleton,
  useRouter,
} from '@main/core-ui';
import type { WorkflowConfig } from '@main/dsr-types';
import { endpoints } from '@main/pc-types';
import type { TakeControlConfirmProps } from '@main/privacy-center/src/components/TakeControlConfirm';
import TakeControlConfirm from '@main/privacy-center/src/components/TakeControlConfirm';
import ActiveDataSubject from '@main/privacy-center/src/containers/ActiveDataSubject';
import { selectActiveDataSubject } from '@main/privacy-center/src/containers/ActiveDataSubject/selectors';
import {
  selectCoreIdentifier,
  selectIsLoggedIn,
  selectIsLoggingIn,
  selectRequiredPrivacyCenter,
} from '@main/privacy-center/src/containers/App/selectors';
import { useAuthorizationUrl } from '@main/privacy-center/src/containers/Login/generateAuthorizationUrl';
import { buildUseDhMutation } from '@main/privacy-center/src/hooks';
import type { LocaleValue } from '@transcend-io/internationalization';
import { RequestAction } from '@transcend-io/privacy-types';
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { usePartition } from '../../hooks/usePartition';
import { writeOauthStateToLocalStorage } from '../TokenLogin/helpers';
import type { DisplayWorkflowsProps } from './DisplayWorkflows';
import { DisplayWorkflows } from './DisplayWorkflows';
import { formToRequestIdentifiers } from './formToRequestIdentifiers';
import { takeControlMessages } from './messages';
import type { TakeControlModalKey } from './selectors';
import {
  SELECT_SUBJECT_MODAL_KEY,
  selectTakeControlModalName,
  TAKE_CONTROL_MODAL_KEY,
} from './selectors';
import { TakeControlTitle } from './TakeControlTitle';
import {
  ButtonWrapper,
  ClassOverrides,
  HeaderWrapper,
  ModalWrapper,
  SelectSubjectModalWrapper,
  StyledModal,
  TakeControlModalWrapper,
  TakeControlWrapper,
} from './wrappers';
import { syncAllConsent, syncSaleOfInfoConsent } from './xdi';

const useMakeDataSubjectRequests = buildUseDhMutation(
  endpoints.makeDataSubjectRequests,
);
const usePrivacyCenterWorkflows = buildUseQuery(
  endpoints.privacyCenterWorkflows,
);

export interface TakeControlProps {
  /** Explicit onExited function with context of modal button ref */
  onExited: () => void;
}

/**
 * Workflows modal set to select subject and workflow to run, or be redirected to login
 */
export const WorkflowsTakeControl = forwardRef<
  HTMLButtonElement,
  TakeControlProps
>(({ onExited }, ref) => {
  const { formatMessage, locale } = useIntl();
  const [submissionCompleted, setSubmissionCompleted] = useState(false);
  const { redirect, location } = useRouter();
  const [makeDataSubjectRequests, { error }] = useMakeDataSubjectRequests();
  const [{ selectedPartitionOption }] = usePartition();
  const coreIdentifier = useSelector(selectCoreIdentifier);
  const activeDataSubject = useSelector(selectActiveDataSubject);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isLoggingIn = useSelector(selectIsLoggingIn);
  const privacyCenter = useSelector(selectRequiredPrivacyCenter);
  const authorizationUrlPayload = useAuthorizationUrl(activeDataSubject);

  const { data } = usePrivacyCenterWorkflows();
  const workflowsList = useMemo(
    () =>
      (data?.nodes ?? []).filter(
        (workflow) =>
          workflow.subject &&
          activeDataSubject &&
          workflow.subject.id === activeDataSubject.id,
      ),
    [data, activeDataSubject],
  );
  const [selectedWorkflowConfigId, setSelectedWorkflowConfigId] =
    useState<WorkflowConfig['id']>();
  const selectedWorkflow = useMemo(
    () => workflowsList.find((wf) => wf.id === selectedWorkflowConfigId),
    [workflowsList, selectedWorkflowConfigId],
  );

  const startingModalKey = useSelector(selectTakeControlModalName);
  const [currentModal, setCurrentModal] = useState<TakeControlModalKey>();

  const submitDSR: TakeControlConfirmProps['confirmRequest'] = useCallback(
    ({
      captcha,
      subjectType,
      type,
      postCompileStatus,
      region,
      details,
      ...rest
    }) =>
      makeDataSubjectRequests?.({
        variables: {
          input: [
            {
              type,
              subjectType,
              details,
              region,
              captcha,
              requestIdentifiers: formToRequestIdentifiers(rest),
              locale: locale as LocaleValue,
              workflowConfigId: selectedWorkflowConfigId,
              partitionKey: selectedPartitionOption?.value,
              ...(postCompileStatus ? { postCompileStatus } : {}),
            },
          ],
        },
        // refresh the active request count and history after submission
        refetchQueries: ['RequestHistoryActiveCount', 'RequestHistory'],
      }).then(({ data }) => {
        if (!data) return;
        privacyCenter.consentManagers.forEach(({ id, configuration }) => {
          const { syncEndpoint } = configuration;
          if (type === RequestAction.TrackingOptOut && syncEndpoint) {
            syncAllConsent(syncEndpoint, id, false);
          }
          if (type === RequestAction.SaleOptOut && syncEndpoint) {
            syncSaleOfInfoConsent(syncEndpoint, id, false);
          }
        });
        setSubmissionCompleted(true);
      }),
    [
      selectedWorkflow,
      privacyCenter,
      selectedPartitionOption,
      locale,
      selectedWorkflowConfigId,
    ],
  );

  const selectWorkflow: DisplayWorkflowsProps['selectWorkflow'] = (
    newlySelectedWorkflowId,
  ) => {
    // If active data subject has oauth, navigate to OAuth route, otherwise
    // immediately navigate to process dsr
    if (authorizationUrlPayload && !isLoggedIn) {
      writeOauthStateToLocalStorage(authorizationUrlPayload.state);
      window.location.replace(authorizationUrlPayload.url.href);
    } else if (!authorizationUrlPayload && !isLoggedIn) {
      setCurrentModal(undefined);
      redirect(
        // this has a more global effect that just tears down the whole component I think
        addQueryParams(location, {
          modal: 'log-in',
        }),
      );
    } else {
      setSelectedWorkflowConfigId(newlySelectedWorkflowId);
    }
  };

  const onExitModals = (): void => {
    setSelectedWorkflowConfigId(undefined);
    setSubmissionCompleted(false);
    onExited();
  };

  if (!activeDataSubject) {
    return null;
  }
  return (
    <TakeControlWrapper>
      <TakeControlModalWrapper>
        <ClassOverrides />
        {privacyCenter.showPrivacyRequestButton && (
          <ButtonWrapper
            buttonType="primary"
            id={`modal-${startingModalKey}`}
            onClick={() => setCurrentModal(startingModalKey)}
            ref={ref}
          >
            {formatMessage(takeControlMessages.takeControl)}
          </ButtonWrapper>
        )}
        <StyledModal
          width="fit-content"
          centered
          onHide={() => setCurrentModal(undefined)}
          onExited={onExitModals}
          show={currentModal === SELECT_SUBJECT_MODAL_KEY}
          label={takeControlMessages.dataSubjectSelectLabel}
        >
          <SelectSubjectModalWrapper>
            <ActiveDataSubject
              onSelect={() => setCurrentModal(TAKE_CONTROL_MODAL_KEY)}
            />
          </SelectSubjectModalWrapper>
        </StyledModal>
        <StyledModal
          width="fit-content"
          centered
          onHide={() => setCurrentModal(undefined)}
          onExited={() => {
            onExitModals();
          }}
          show={activeDataSubject && currentModal === TAKE_CONTROL_MODAL_KEY}
          label={takeControlMessages.title}
        >
          {isLoggingIn && (
            <ModalWrapper>
              <TakeControlTitle
                isLoggedIn={isLoggedIn}
                activeDataSubject={activeDataSubject}
              />
              <Skeleton paragraph={false} />
              <Skeleton paragraph />
              <Skeleton paragraph />
            </ModalWrapper>
          )}
          {isLoggedIn && selectedWorkflow && (
            <TakeControlConfirm
              coreIdentifier={coreIdentifier?.value || ''}
              activeDataSubjectId={activeDataSubject.id}
              goBack={() => {
                setSelectedWorkflowConfigId(undefined);
                setSubmissionCompleted(false);
              }}
              confirmRequest={submitDSR}
              subjectType={activeDataSubject.type}
              submittedSuccessfully={submissionCompleted && !error}
              error={error}
              action={{
                ...selectedWorkflow.action,
                // Inject the form items, for backwards compatibility
                formItems: selectedWorkflow.formItems,
              }}
              workflow={selectedWorkflow}
            />
          )}
          {!selectedWorkflow && !isLoggingIn && (
            <ModalWrapper>
              <TakeControlTitle
                isLoggedIn={isLoggedIn}
                activeDataSubject={activeDataSubject}
              />
              {!activeDataSubject.disabledInstructions && (
                <>
                  <HeaderWrapper>
                    {formatMessage(takeControlMessages.header)}
                  </HeaderWrapper>
                  <DisplayWorkflows
                    workflows={workflowsList}
                    selectWorkflow={selectWorkflow}
                  />
                </>
              )}
            </ModalWrapper>
          )}
        </StyledModal>
      </TakeControlModalWrapper>
    </TakeControlWrapper>
  );
});

export default WorkflowsTakeControl;
